import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  SalesOrder,
  SalesOrderStatus,
  CreateSalesOrderRequest,
  SalesOrderSortField,
} from '../types/sales.types';
import { salesService } from '../services/sales.service';

export interface QuoteListParams {
  page: number;
  limit: number;
  search?: string;
  sortBy?: SalesOrderSortField;
  sortOrder?: 'ASC' | 'DESC';
}

// Quotes are sales orders in the "quote" status, so they share the paginated endpoint
export const useQuotesPage = (params: QuoteListParams) => {
  return useQuery({
    queryKey: ['salesOrders', 'quotes', params],
    queryFn: () => salesService.getPage({ ...params, status: SalesOrderStatus.QUOTE }),
    placeholderData: keepPreviousData,
  });
};

// Every quote change also affects the sales order list and its totals
const useInvalidateSales = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
};

export const useCreateQuote = () => {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: (data: CreateSalesOrderRequest) => salesService.createQuote(data),
    onSuccess: invalidate,
  });
};

export const useUpdateQuote = () => {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSalesOrderRequest> }) =>
      salesService.updateQuote(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteQuote = () => {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: (id: string) => salesService.deleteQuote(id),
    onSuccess: invalidate,
  });
};

export const useConvertQuote = () => {
  const invalidate = useInvalidateSales();
  return useMutation({
    mutationFn: (id: string) => salesService.convertQuoteToOrder(id),
    onSuccess: invalidate,
  });
};

// The fields a new quote needs, copied from an existing one
export const toQuoteRequest = (quote: SalesOrder): CreateSalesOrderRequest => ({
  // A copy stays in the plant of the quote it was made from
  plantId: quote.plantId ?? undefined,
  customerId: quote.customer?.id || quote.customerId,
  orderDate: new Date().toISOString(),
  expectedDeliveryDate: quote.expectedDeliveryDate,
  items: quote.items.map((item) => ({
    productId: item.productId,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    discountRate: item.discountRate || 0,
    // Without a rate the server uses the default of the GST rate master
    taxRate: item.taxRate ?? undefined,
  })),
  notes: quote.notes || undefined,
  shippingAddress: quote.shippingAddress || undefined,
  deliveryTerms: quote.deliveryTerms || undefined,
  paymentTerms: quote.paymentTerms || undefined,
  shippingMethod: quote.shippingMethod || undefined,
  deliveryLocation: quote.deliveryLocation || undefined,
  specialInstructions: quote.specialInstructions || undefined,
  promisedDate: quote.promisedDate || undefined,
});

export const useQuote = (id: string) => {
  const [quote, setQuote] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await salesService.getById(id);
      setQuote(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch quote');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchQuote();
    }
  }, [id]);

  const updateQuote = async (data: Partial<CreateSalesOrderRequest>): Promise<SalesOrder | null> => {
    try {
      setError(null);
      const updatedQuote = await salesService.updateQuote(id, data);
      setQuote(updatedQuote);
      return updatedQuote;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update quote');
      return null;
    }
  };

  const convertToOrder = async (): Promise<{ success: boolean; message?: string; order?: SalesOrder }> => {
    try {
      setError(null);
      const order = await salesService.convertQuoteToOrder(id);
      return { success: true, order };
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to convert quote to order');
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to convert quote to order'
      };
    }
  };

  const refreshQuote = () => {
    fetchQuote();
  };

  return {
    quote,
    loading,
    error,
    updateQuote,
    convertToOrder,
    refreshQuote,
  };
};
